/**
 * The public contact form — `POST /api/public/contact`.
 *
 * Anonymous and rate-limited server-side ("contact-form" policy). A submission is STORED whether or
 * not the notification email to council@ goes out, so a 200 means "we have it", not "an email was
 * sent" — the UI must not promise the second thing.
 */

const isProduction = import.meta.env.PROD;

/**
 * Base URL for an anonymous `/api/public/...` endpoint.
 *
 * Mirrors `marketplaceEndpoint` in marketplaceApi.ts and `daStatsEndpoint` in PartnerNetwork.tsx —
 * which is two copies of this already. Worth collapsing into one helper the three of them share;
 * not done here because changing how the marketplace and stats calls resolve their host is a
 * bigger blast radius than adding a contact form warrants.
 */
export function publicEndpoint(path: string): string {
    const clean = path.replace(/^\/+|\/+$/g, '');

    if (isProduction) {
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        return `${origin}/api/public/${clean}`;
    }

    const apiServerUrl = import.meta.env.VITE_API_SERVER_URL;
    if (apiServerUrl) {
        const base = apiServerUrl.replace(/\/+$/, '');
        const withApi = base.includes('/api') ? base : `${base}/api`;
        return `${withApi}/public/${clean}`;
    }

    return `http://localhost:5183/api/public/${clean}`;
}

/** Mirrors `PublicContactController.ContactFormRequest`. Lengths match its attributes. */
export interface ContactFormRequest {
    name: string;
    email: string;
    phone?: string;
    business?: string;
    enquiryCategory?: string;
    message: string;
}

/** The server's own field limits, so the form can stop a submission it is going to reject. */
export const CONTACT_LIMITS = {
    name: 200,
    email: 256,
    phone: 30,
    business: 200,
    enquiryCategory: 100,
    message: 5000,
} as const;

export class ContactFormError extends Error {
    /** True when the rate limiter turned it away, which needs different advice from a validation error. */
    readonly rateLimited: boolean;

    constructor(message: string, rateLimited = false) {
        super(message);
        this.name = 'ContactFormError';
        this.rateLimited = rateLimited;
    }
}

/**
 * Sends the form.
 *
 * <p>Throws {@link ContactFormError} carrying the SERVER's message where it gave one — it knows why
 * it refused ("Name contains invalid characters") and this side does not. A thrown error always
 * means nothing was stored; resolving means it was.</p>
 */
export async function submitContactForm(request: ContactFormRequest): Promise<void> {
    let response: Response;

    try {
        response = await fetch(publicEndpoint('contact'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(request),
        });
    } catch {
        // Offline, DNS, CORS — never a validation problem, and telling somebody to check their
        // details when their wifi dropped sends them looking in the wrong place.
        throw new ContactFormError(
            "We couldn't reach us just then. Check your connection and try again.");
    }

    if (response.ok) return;

    if (response.status === 429) {
        throw new ContactFormError(
            "That's a few messages in a short time. Please wait a minute and try again.", true);
    }

    // The body may be the controller's { error }, ASP.NET's ModelState { errors: {...} }, or —
    // from a proxy — HTML. Take the most specific thing that is actually there.
    let message: string | null = null;
    try {
        const body = await response.json();
        if (typeof body?.error === 'string') {
            message = body.error;
        } else if (body?.errors && typeof body.errors === 'object') {
            const first = Object.values(body.errors as Record<string, unknown>)
                .flatMap((v) => (Array.isArray(v) ? v : [v]))
                .find((v): v is string => typeof v === 'string' && v.length > 0);
            message = first ?? null;
        }
    } catch {
        // Not JSON. Fall through to the generic message rather than showing the reader a page of
        // markup.
    }

    throw new ContactFormError(
        message ?? "We couldn't send that just then. Please try again.");
}
